import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface CreativeRow {
  readonly creativeAssetId: string;
  readonly creativeCode: string;
  readonly title: string;
  readonly creativeType: string;
  readonly version: string;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'UNDER_REVIEW', 'PENDING_APPROVAL', 'APPROVED', 'PUBLISHED', 'ARCHIVED', 'RETIRED'];
const CREATIVE_TYPES = ['', 'CREATIVE_CONCEPT', 'DESIGN_FILE', 'ARTWORK_FILE', 'PRODUCTION_FILE', 'FINAL_APPROVED', 'PRINT_READY', 'DIGITAL_PUBLISHING', 'SOURCE_FILE'];
const CREATE_TYPES = ['CREATIVE_CONCEPT', 'DESIGN_FILE', 'ARTWORK_FILE', 'PRODUCTION_FILE', 'FINAL_APPROVED', 'PRINT_READY', 'DIGITAL_PUBLISHING', 'SOURCE_FILE'];

/**
 * M36 — Creative Library (§31). Register of creative studio assets (T484) —
 * concepts, design/artwork/production files and print/digital deliverables —
 * across the 8 creative types and the draft → published lifecycle. Filterable
 * by creative type, status and a free-text search. Tenant isolation is
 * enforced server-side (LAW 4).
 */
export function CreativeLibrary(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<CreativeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [creativeType, setCreativeType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState(CREATE_TYPES[0]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (creativeType) params.set('creativeType', creativeType);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: CreativeRow[]; total: number }>(`/v1/dam/creatives${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, creativeType, status, q, reload]);

  const on403 = (err: unknown, perm: string): void => {
    if (err instanceof ApiError && err.status === 403) setNotice(t('m36.common.permRequired', { perm }));
    else setNotice((err as Error).message);
  };

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setNotice(null);
    if (!newTitle.trim()) { setNotice(t('m36.common.titleReq')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/dam/creatives`, {
        method: 'POST',
        body: { title: newTitle.trim(), creativeType: newType },
        token,
      });
      setNotice(t('m36.creative.created', { name: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      on403(err, 'M36_CREATIVE_MANAGE');
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: CreativeRow, action: string, label: string, perm: string): Promise<void> => {
    setNotice(null);
    setBusyId(r.creativeAssetId);
    try {
      await apiRequest(`/v1/dam/creatives/${r.creativeAssetId}/${action}`, { method: 'POST', body: {}, token });
      setNotice(t('m36.common.codeLabel', { code: r.creativeCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      on403(err, perm);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="creative-library">
      <h1>{t('m36.creative.title')}</h1>
      <form aria-label="create-creative-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m36.common.title')}
          <input aria-label="field-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
        </label>
        <label>
          {t('m36.common.type')}
          <select aria-label="field-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
            {CREATE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <Button variant="primary" type="submit" aria-label="create-creative" disabled={busy}>{t('m36.creative.create')}</Button>
      </form>
      {notice && <p role="status" aria-label="creative-notice">{notice}</p>}
      <div className="filters">
        <label>
          {t('m36.common.type')}
          <select aria-label="filter-type" value={creativeType} onChange={(e) => setCreativeType(e.target.value)}>
            {CREATIVE_TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m36.common.titleKeywordPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="creative-count">{t('m36.creative.count', { count: total })}</p>
          <DataTable
            ariaLabel="creative-table"
            rows={rows}
            rowKey={(r) => r.creativeAssetId}
            columns={[
              { key: 'creativeCode', header: t('m36.common.code'), render: (r) => r.creativeCode },
              { key: 'title', header: t('m36.common.title'), render: (r) => r.title },
              { key: 'creativeType', header: t('m36.common.type'), render: (r) => r.creativeType },
              { key: 'version', header: t('m36.common.version'), render: (r) => r.version },
              { key: 'status', header: t('m36.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m36.common.actions'), render: (r) => (
                <>
                    {r.status === 'DRAFT' && (
                      <Button variant="secondary" type="button" aria-label={`submit-${r.creativeCode}`} disabled={busyId === r.creativeAssetId} onClick={() => void act(r, 'submit', t('m36.creative.act.submitted'), 'M36_CREATIVE_MANAGE')}>{t('m36.common.submit')}</Button>
                    )}
                    {(r.status === 'UNDER_REVIEW' || r.status === 'PENDING_APPROVAL') && (
                      <Button variant="secondary" type="button" aria-label={`approve-${r.creativeCode}`} disabled={busyId === r.creativeAssetId} onClick={() => void act(r, 'approve', t('m36.creative.act.approved'), 'M36_CREATIVE_APPROVE')}>{t('m36.common.approve')}</Button>
                    )}
                    {r.status === 'APPROVED' && (
                      <Button variant="primary" type="button" aria-label={`publish-${r.creativeCode}`} disabled={busyId === r.creativeAssetId} onClick={() => void act(r, 'publish', t('m36.creative.act.published'), 'M36_CREATIVE_APPROVE')}>{t('m36.common.publish')}</Button>
                    )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
